"use client"
import Link from "next/link"
import Image from "next/image"
import { useEffect, useState } from "react"
import { sellerDisplayName } from "@/lib/accounts/display-name"
import SellerLink from "@/components/SellerLink"
import { MAX_TRUST_SCORE } from "@/lib/trust/constants"

const CITIES = ["Harare", "Bulawayo", "Mutare", "Gweru", "Victoria Falls", "Masvingo", "Kwekwe", "Chinhoyi", "Bindura", "Marondera"]
const TYPES: [string, string][] = [
  ["", "All types"],
  ["RESIDENTIAL", "Residential"],
  ["LAND", "Land / Stands"],
  ["COMMERCIAL", "Commercial"],
  ["MIXED", "Mixed use"],
]

export default function DevelopersPage() {
  const [projects, setProjects] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [city, setCity] = useState("")
  const [projectType, setProjectType] = useState("")
  const [q, setQ] = useState("")
  const [searchInput, setSearchInput] = useState("")

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams({ page: String(page), pageSize: "12" })
    if (city) params.set("city", city)
    if (projectType) params.set("projectType", projectType)
    if (q) params.set("q", q)
    fetch("/api/developers?" + params.toString())
      .then(r => r.json())
      .then(d => { setProjects(d.data ?? []); setTotalPages(d.totalPages ?? 1); setTotal(d.total ?? 0); setLoading(false) })
      .catch(() => setLoading(false))
  }, [page, city, projectType, q])

  const hasFilters = !!(city || projectType || q)
  function clearAll() { setCity(""); setProjectType(""); setQ(""); setSearchInput(""); setPage(1) }

  const pill = (active: boolean): React.CSSProperties => ({
    padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${active ? "var(--navy)" : "var(--line)"}`,
    background: active ? "var(--navy)" : "var(--cream)", color: active ? "var(--cream)" : "var(--navy)",
    fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
  })

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <div style={{ background: "var(--navy)", padding: "64px 24px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <span className="eyebrow" style={{ background: "rgba(200,150,62,0.15)", borderColor: "rgba(200,150,62,0.3)", color: "var(--gold)" }}>Off-plan and new developments</span>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 48, fontWeight: 700, color: "var(--cream)", margin: "20px 0 12px", lineHeight: 1.1 }}>
            New <em style={{ color: "var(--gold)" }}>developments</em>.
          </h1>
          <p style={{ color: "rgba(253,251,247,0.75)", fontSize: 16, maxWidth: 560 }}>
            Browse verified off-plan developments. Join the waitlist and be first in line when units open.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ background: "var(--cream-warm)", borderBottom: "1px solid var(--line)", padding: "16px 24px", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <form onSubmit={e => { e.preventDefault(); setQ(searchInput.trim()); setPage(1) }} style={{ flex: "1 1 220px", minWidth: 180 }}>
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search developments…" aria-label="Search developments"
              style={{ width: "100%", padding: "9px 14px", border: "1px solid var(--line)", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
          </form>
          <select value={city} onChange={e => { setCity(e.target.value); setPage(1) }} aria-label="Filter by city"
            style={{ padding: "9px 14px", border: "1px solid var(--line)", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none", cursor: "pointer" }}>
            <option value="">All cities</option>
            {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {TYPES.map(([v, label]) => (
              <button key={v} type="button" onClick={() => { setProjectType(v); setPage(1) }} style={pill(projectType === v)}>{label}</button>
            ))}
          </div>
          {hasFilters && <button type="button" onClick={clearAll} style={{ fontSize: 13, color: "var(--gold-deep)", background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Clear all</button>}
        </div>
      </div>

      <div className="section">
        {!loading && (
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>
            {total} development{total !== 1 ? "s" : ""}{hasFilters ? " match your filters" : ""}
          </div>
        )}
        {loading ? (
          <div className="listings-grid">
            {[0, 1, 2].map(i => <div key={i} style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, height: 320, opacity: 0.5 }} />)}
          </div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 24px", background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16 }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🏗️</div>
            <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 26, color: "var(--navy)", marginBottom: 10 }}>{hasFilters ? "No developments match" : "No developments listed yet"}</h2>
            <p style={{ color: "var(--muted)", fontSize: 14, maxWidth: 420, margin: "0 auto 28px", lineHeight: 1.7 }}>
              {hasFilters ? "Try widening your search or clearing the filters." : "Verified off-plan developments will appear here. Are you a property developer in Zimbabwe?"}
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              {hasFilters
                ? <button onClick={clearAll} style={{ padding: "12px 22px", background: "var(--navy)", color: "var(--cream)", borderRadius: 10, border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Clear filters</button>
                : <>
                    <Link href="/auth/register" style={{ padding: "12px 22px", background: "var(--navy)", color: "var(--cream)", borderRadius: 10, textDecoration: "none", fontWeight: 700, fontSize: 14 }}>Register as a developer</Link>
                    <Link href="/contact" style={{ padding: "12px 22px", background: "transparent", border: "1px solid var(--line)", borderRadius: 10, textDecoration: "none", fontWeight: 600, fontSize: 14, color: "var(--navy)" }}>Contact us</Link>
                  </>}
            </div>
          </div>
        ) : (
          <>
            <div className="listings-grid">
              {projects.map(p => {
                const available = p.availableUnits ?? 0
                const totalU = p.totalUnits ?? 0
                const pct = totalU > 0 ? Math.round(((totalU - available) / totalU) * 100) : 0
                return (
                  <Link key={p.id} href={"/developers/" + p.id} className="listing-card" style={{ textDecoration: "none" }}>
                    <div className="listing-img" style={{ background: p.coverImage ? undefined : "linear-gradient(135deg,#2a3f6e,#1B2D54)" }}>
                      {p.coverImage && <Image src={p.coverImage} alt={p.name} fill sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" style={{ objectFit: "cover" }} />}
                      <span className="listing-tag gold">OFF-PLAN</span>
                      {p.trustScore > 0 && (
                        <span style={{ position: "absolute", top: 10, right: 10, background: "rgba(27,45,84,0.85)", color: "var(--green-text)", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6 }}>Trust {p.trustScore}/{MAX_TRUST_SCORE}</span>
                      )}
                      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "40px 16px 14px", background: "linear-gradient(to top, rgba(14,22,42,0.95), transparent)" }}>
                        <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: "#FDFBF7" }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: "rgba(253,251,247,0.65)", marginTop: 2 }}>{p.suburb}, {p.city}</div>
                      </div>
                    </div>
                    <div className="listing-body">
                      {p.minPrice && (
                        <div className="listing-price">
                          ${p.minPrice.toLocaleString()}{p.maxPrice && p.maxPrice !== p.minPrice ? `, $${p.maxPrice.toLocaleString()}` : ""} <em>USD</em>
                        </div>
                      )}
                      <div className="listing-title">{p.name}</div>
                      <div className="listing-loc">{p.suburb}, {p.city}</div>
                      {p.developer && (
                        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                          by <SellerLink agent={p.developer} nested style={{ color: "var(--gold-deep)", fontWeight: 600 }}>{sellerDisplayName(p.developer, "Developer")}</SellerLink>
                        </div>
                      )}
                      {p.permitVerified && p.parentDeedVerified && p.complianceCertVerified && (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, fontSize: 11, fontWeight: 700, color: "#3d8b5f", background: "rgba(61,139,95,0.12)", padding: "3px 9px", borderRadius: 20 }}>
                          <span style={{ display: "inline-grid", placeItems: "center", width: 14, height: 14, borderRadius: "50%", background: "#3d8b5f", color: "white", fontSize: 8 }}>✓</span>
                          Fully compliant
                        </div>
                      )}
                      {p.description && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6, lineHeight: 1.5 }}>{p.description.substring(0, 100)}{p.description.length > 100 ? "..." : ""}</div>}
                      <div className="listing-meta" style={{ marginTop: 10 }}>
                        <span>{totalU} units total</span>
                        <span style={{ color: available > 0 ? "var(--green)" : "var(--red)", fontWeight: 600 }}>{available} available</span>
                      </div>
                      {totalU > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>
                            <span>{pct}% reserved</span>
                            {p.completionDate && <span>Est. {new Date(p.completionDate).getFullYear()}</span>}
                          </div>
                          <div style={{ height: 4, background: "var(--line)", borderRadius: 2, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: pct + "%", background: pct >= 80 ? "var(--red)" : pct >= 50 ? "var(--gold)" : "var(--green)", transition: "width 0.5s" }} />
                          </div>
                        </div>
                      )}
                      <div style={{ marginTop: 12, padding: "8px 14px", background: "var(--navy)", color: "var(--cream)", borderRadius: 8, fontSize: 12, fontWeight: 700, textAlign: "center" }}>
                        View project
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: 40 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  style={{ padding: "10px 18px", border: "1px solid var(--line)", borderRadius: 10, background: page <= 1 ? "var(--cream-warm)" : "var(--cream)", color: page <= 1 ? "var(--muted)" : "var(--navy)", fontWeight: 600, fontSize: 14, cursor: page <= 1 ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                  ← Prev
                </button>
                <span style={{ fontSize: 14, color: "var(--muted)" }}>Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  style={{ padding: "10px 18px", border: "1px solid var(--line)", borderRadius: 10, background: page >= totalPages ? "var(--cream-warm)" : "var(--cream)", color: page >= totalPages ? "var(--muted)" : "var(--navy)", fontWeight: 600, fontSize: 14, cursor: page >= totalPages ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
